/**
 * ------------------------------------------------------------------
 * Live Kaching bundle prices for the "Collection products" grid.
 * ------------------------------------------------------------------
 * Same mechanism as the cart drawer's upsell deal prices
 * (assets/upsell-simple.js): Kaching's per-unit deal price cannot be seen by
 * Liquid, because the discount is applied by a Shopify Function at cart time.
 * The only server-rendered copy of the deal config is the JSON the app embeds
 * in each PRODUCT page:
 *
 *   <script class="kaching-bundles-deal-block-settings"
 *           data-product-id="123" type="application/json">{...}</script>
 *
 * one block per market AND per A/B-test arm. We fetch each card's product page
 * same-origin, so the shopper's own market, currency and A/B session apply,
 * and read the qty-1 deal price for the active presentment currency.
 *
 * SAFETY RULES (must never under-promise a price — identical to the cart):
 * - With multiple A/B arms, display the MOST EXPENSIVE arm's qty-1 price. A
 *   shopper in the cheaper arm gets a pleasant surprise; one in the expensive
 *   arm sees exactly what they will pay.
 * - If ANY arm is unevaluable (unknown discount type / bad value) the maximum
 *   is unknowable -> fall back to the Liquid price.
 * - Missing embed, empty embed, parse failure, or an implausible price
 *   (<= 0, or not below the list price) -> fall back to the Liquid price.
 * - Every failure path degrades silently. If Kaching changes its embed format
 *   the grid simply keeps showing Shopify prices.
 *
 * Cache key is kaching_deal_v3_<id>_<currency>_<mode>. The mode is part of it
 * because two grids may ask for different tiers of the same product.
 */
(function () {
  "use strict";

  var DEAL_CACHE_TTL = 10 * 60 * 1000; // ~10 minutes
  var MAX_CONCURRENT = 4;
  var inFlight = {};
  var queue = [];
  var active = 0;

  function getPresentmentCurrency() {
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
    } catch (e) {
      /* noop */
    }
    var meta = document.querySelector('meta[name="currency"]');
    return meta ? meta.content : null;
  }

  function formatDealMoney(amount, currencyCode) {
    try {
      if (typeof Shopify !== "undefined" && Shopify.formatMoney && Shopify.money_format) {
        return Shopify.formatMoney(Math.round(amount * 100), Shopify.money_format);
      }
    } catch (e) {
      /* fall through */
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: currencyCode || "USD",
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  /**
   * Price one deal bar, returning { total, units } in major units, or null when
   * the bar cannot be evaluated.
   *
   * Kaching's "specific" values are the TOTAL for that portion's quantity, not a
   * per-unit price. Verified against live config: the eye cream's "2 Tubes + 1
   * Free" is buy2 specific=69.90 (= 2 x its 34.95 single price) get1
   * percentage=100, i.e. 3 units for 69.90. "percentage" is a discount off the
   * list price and IS per unit.
   */
  function priceBar(bar, listPrice) {
    if (!bar) return null;

    function portion(type, value, qty) {
      var v = Number(value);
      if (!isFinite(v) || !(qty > 0)) return null;
      if (type === "specific") return v; // total for these qty units
      if (type === "percentage") return qty * listPrice * (1 - v / 100);
      return null; // unknown type -> unevaluable
    }

    if (bar.dealBarType === "quantity-break") {
      var q = Number(bar.quantity);
      var total = portion(bar.discountType, bar.discountValue, q);
      if (total == null) return null;
      return { total: total, units: q };
    }

    if (bar.dealBarType === "bxgy") {
      var bq = Number(bar.buyQuantity);
      var gq = Number(bar.getQuantity);
      var buy = portion(bar.buyDiscountType, bar.buyDiscountValue, bq);
      var get = portion(bar.getDiscountType, bar.getDiscountValue, gq);
      if (buy == null || get == null) return null;
      return { total: buy + get, units: bq + gq };
    }

    return null; // other bar types are not priced
  }

  /**
   * Pick the per-unit price for one deal block, according to `mode`:
   *   single      - the single-unit bar (what one jar costs on its own)
   *   preselected - the bundle Kaching highlights by default
   *   cheapest    - the lowest per-unit price of any bundle on the block
   * Returns a number (major units) or null.
   */
  function priceBlock(cfg, listPrice, mode) {
    var bars = cfg.dealBars;
    if (!Array.isArray(bars) || !bars.length) return null;

    if (mode === "preselected") {
      var chosen = null;
      for (var i = 0; i < bars.length; i++) {
        if (bars[i] && bars[i].id === cfg.preselectedDealBarId) chosen = bars[i];
      }
      if (!chosen) return null;
      var pp = priceBar(chosen, listPrice);
      if (!pp || !(pp.units > 0)) return null;
      return Math.round((pp.total / pp.units) * 100) / 100;
    }

    if (mode === "cheapest") {
      var best = null;
      for (var j = 0; j < bars.length; j++) {
        var r = priceBar(bars[j], listPrice);
        if (!r || !(r.units > 0)) continue; // skip bars we cannot price
        var per = r.total / r.units;
        if (best == null || per < best) best = per;
      }
      if (best == null) return null;
      return Math.round(best * 100) / 100;
    }

    // default: "single" — the one-unit bar only
    for (var k = 0; k < bars.length; k++) {
      var b = bars[k];
      if (b && b.dealBarType === "quantity-break" && Number(b.quantity) === 1) {
        var s = priceBar(b, listPrice);
        if (!s || !(s.units > 0)) return null;
        return Math.round((s.total / s.units) * 100) / 100;
      }
    }
    return null;
  }

  /**
   * Parse the Kaching price for a product out of its page HTML.
   * Returns a number (major units) or null when no trustworthy price exists.
   */
  function parseKachingDealPrice(html, productId, currencyCode, listPrice, mode) {
    try {
      if (!html || !productId || !currencyCode || !(listPrice > 0)) return null;

      var matched = {}; // arms of blocks scoped to this exact currency
      var generic = {}; // arms of blocks carrying no currency at all
      var re = /<script([^>]*class="kaching-bundles-deal-block-settings"[^>]*)>([\s\S]*?)<\/script>/g;
      var m;
      while ((m = re.exec(html)) !== null) {
        var attrs = m[1];
        var body = (m[2] || "").trim();
        if (!body) continue; // has the app block but no active deal

        var pidMatch = attrs.match(/data-product-id="(\d+)"/);
        if (!pidMatch || pidMatch[1] !== String(productId)) continue;

        var cfg;
        try {
          cfg = JSON.parse(body);
        } catch (e) {
          continue;
        }
        if (!cfg) continue;

        // A block may be scoped to a currency, or carry none at all (a single
        // config serving every market — the neck mask is set up this way).
        var bucket;
        if (cfg.currency && cfg.currency.currencyCode) {
          if (cfg.currency.currencyCode !== currencyCode) continue;
          bucket = matched;
        } else {
          bucket = generic;
        }
        bucket[cfg.abTestVariantId || "default"] = priceBlock(cfg, listPrice, mode);
      }

      // Currency-scoped blocks win outright; the currency-less config is the
      // fallback, never a peer, so a foreign-currency number can never leak in.
      var arms = Object.keys(matched).length ? matched : generic;
      var keys = Object.keys(arms);
      if (keys.length === 0) return null;

      // Never under-promise: across A/B arms show the MOST EXPENSIVE arm's
      // price. If any arm is unevaluable the maximum is unknowable -> fall back.
      var maxPrice = null;
      for (var n = 0; n < keys.length; n++) {
        var p = arms[keys[n]];
        if (p == null) return null;
        if (maxPrice == null || p > maxPrice) maxPrice = p;
      }

      if (!(maxPrice > 0) || !(maxPrice < listPrice - 0.001)) return null;
      return maxPrice;
    } catch (e) {
      return null;
    }
  }

  function applyToCard(card, price, currency) {
    if (price == null) return;
    var amountEl = card.querySelector(".cpg-price-amount");
    if (!amountEl) return;
    // Optional fixed prefix (e.g. "A$"), so a card can match prices written beside it in
    // Liquid; without the attribute the store's money format is used, as before.
    var prefix = card.getAttribute("data-currency-prefix");
    amountEl.textContent = prefix ? prefix + price.toFixed(2) : formatDealMoney(price, currency);

    var strikeMode = card.getAttribute("data-strike-source");
    if (strikeMode === "none") return;

    var list = parseFloat(card.getAttribute("data-list-price")) || 0;
    var compare = parseFloat(card.getAttribute("data-compare-price")) || 0;
    var strike = strikeMode === "compare" ? compare : Math.max(list, compare);

    var cmpEl = card.querySelector(".cpg-compare");
    if (cmpEl && strike > price + 0.005) {
      cmpEl.textContent = formatDealMoney(strike, currency);
      cmpEl.hidden = false;
    }

    // Live "SAVE X%" pill (2026-11-03): the saving of this card's Kaching price against the same struck-through
    // price, rounded down so it never over-promises. Stays hidden unless there is a real saving.
    var saveEl = card.querySelector(".cpg-save[data-save-text]");
    if (saveEl && strike > price + 0.005) {
      var pct = Math.floor(((strike - price) / strike) * 100);
      if (pct > 0) {
        saveEl.textContent = saveEl.getAttribute("data-save-text").replace("[percent]", pct);
        saveEl.hidden = false;
      }
    }
  }

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return undefined;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > DEAL_CACHE_TTL) return undefined;
      return entry.p;
    } catch (e) {
      return undefined;
    }
  }

  function writeCache(key, price) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ p: price, t: Date.now() }));
    } catch (e) {
      /* storage blocked: refetch next time */
    }
  }

  function pump() {
    while (active < MAX_CONCURRENT && queue.length) {
      var job = queue.shift();
      active++;
      job(function () {
        active--;
        pump();
      });
    }
  }

  function resolveCard(card, currency) {
    var handle = card.getAttribute("data-product-handle");
    var productId = card.getAttribute("data-product-id");
    var listPrice = parseFloat(card.getAttribute("data-list-price"));
    if (!handle || !productId || !(listPrice > 0)) return;
    if (card.getAttribute("data-deal-done") === "1") return;
    card.setAttribute("data-deal-done", "1");

    var mode = card.getAttribute("data-bundle-mode") || "single";
    // mode is part of the key: two grids may ask for different tiers.
    var cacheKey = "kaching_deal_v3_" + productId + "_" + currency + "_" + mode;
    var cached = readCache(cacheKey);
    if (cached !== undefined) {
      applyToCard(card, cached, currency);
      return;
    }

    queue.push(function (done) {
      if (inFlight[cacheKey]) {
        done();
        return;
      }
      inFlight[cacheKey] = true;
      // ?view=kaching-deal: head + app embeds only (same deal JSON, a fifth of the bytes);
      // falls back to the normal product page by itself if that template is removed.
      fetch("/products/" + handle + "?view=kaching-deal", { credentials: "same-origin" })
        .then(function (r) {
          return r.ok ? r.text() : null;
        })
        .then(function (html) {
          var price = html
            ? parseKachingDealPrice(html, productId, currency, listPrice, mode)
            : null;
          writeCache(cacheKey, price);
          document
            .querySelectorAll(
              '.cpg-card[data-product-id="' + productId + '"][data-bundle-price="1"]' +
                '[data-bundle-mode="' + mode + '"]'
            )
            .forEach(function (c) {
              applyToCard(c, price, currency);
            });
        })
        .catch(function () {
          /* network error: keep the Liquid price */
        })
        .finally(function () {
          delete inFlight[cacheKey];
          done();
        });
    });
    pump();
  }

  function isNearViewport(el) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    // 300px of slack so a card resolves just before it scrolls in
    return r.bottom > -300 && r.top < h + 300;
  }

  function init() {
    var currency = getPresentmentCurrency();
    if (!currency) return;
    var cards = [].slice.call(
      document.querySelectorAll('.cpg-card[data-bundle-price="1"]')
    );
    if (!cards.length) return;

    // Only look up cards the shopper can actually see, so a long grid does not
    // fire a request per product on load. A plain rect test is used rather than
    // IntersectionObserver: it needs no callback delivery, works identically in
    // every browser, and is trivial to reason about.
    var sweepTimer = null;
    function sweep() {
      sweepTimer = null;
      var remaining = false;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i];
        if (c.getAttribute("data-deal-done") === "1") continue;
        if (isNearViewport(c)) {
          resolveCard(c, currency);
        } else {
          remaining = true;
        }
      }
      if (!remaining) {
        window.removeEventListener("scroll", schedule);
        window.removeEventListener("resize", schedule);
      }
    }
    function schedule() {
      if (sweepTimer) return;
      sweepTimer = setTimeout(sweep, 120);
    }

    sweep();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
  }

  /*
   * Shared with the product pages (2026-11-03, snippets/zyren-gallery-badges.liquid): the best saving, in whole
   * percent rounded down, that this page's own Kaching deal block gives for `productId` against `listPrice`
   * (major units, the shopper's currency), or null when no trustworthy number exists. Same parsing as the cards.
   */
  window.zyrenKachingSavePct = function (productId, listPrice, mode) {
    var currency = getPresentmentCurrency();
    if (!currency || !(listPrice > 0)) return null;
    var html = "";
    document.querySelectorAll("script.kaching-bundles-deal-block-settings").forEach(function (sc) {
      html += sc.outerHTML;
    });
    var price = parseKachingDealPrice(html, productId, currency, listPrice, mode || "cheapest");
    if (price == null || !(price < listPrice)) return null;
    var pct = Math.floor(((listPrice - price) / listPrice) * 100);
    return pct > 0 ? pct : null;
  };
  try { document.dispatchEvent(new CustomEvent("zyren:kaching-save-ready")); } catch (e) {}

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
